import {
  type ResearchReportListItem,
  useAttachReport,
  useAttachedReportIds,
  useResearchReports
} from "@app/queries/research";
import Icon from "@lib/common/components/Icon/Icon";
import TimeAgo from "@lib/common/components/TimeAgo/TimeAgo";
import ModalContent from "@lib/common/components/modals/ModalContent";
import ModalHeader from "@lib/common/components/modals/ModalHeader";
import { notifications } from "@lib/notification/notification";
import { Alert, Button, Group, Stack, Text, TextInput, Tooltip } from "@mantine/core";
import { useDebouncedCallback } from "@mantine/hooks";
import { DataTable } from "mantine-datatable";
import { useMemo, useState } from "react";

export default function AttachReportModalContent({
  experimentId,
  onClose
}: {
  experimentId: string;
  onClose: () => void;
}) {
  const [nameFilter, setNameFilter] = useState("");
  const [search, setSearch] = useState<string | undefined>(undefined);

  const { data, isFetching, isError, error } = useResearchReports(undefined, search);
  const attachedIdsQuery = useAttachedReportIds(experimentId);
  const attachedIds = new Set(attachedIdsQuery.data?.report_ids ?? []);
  const attachMutation = useAttachReport();

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setSearch(value || undefined);
  }, 300);

  const handleAttach = (reportId: string) => {
    attachMutation.mutate(
      { params: { path: { report_id: reportId } }, body: { experiment_id: experimentId } },
      {
        onSuccess: onClose,
        onError: () => notifications.error("Couldn't attach report", "It may already be attached to this chat.")
      }
    );
  };

  const columns = useMemo(
    () => [
      {
        accessor: "origin_experiment_name",
        title: "Experiment",
        width: "100%",
        ellipsis: true,
        render: (item: ResearchReportListItem) => (
          <Group gap="xs" align="center" wrap="nowrap">
            <Icon name="file-lines" size="0.9rem" color="dimmed" />
            <Text>{item.origin_experiment_name ?? "Unnamed experiment"}</Text>
          </Group>
        )
      },
      {
        accessor: "created_at",
        title: "Created",
        ellipsis: true,
        render: ({ created_at }: ResearchReportListItem) => (
          <TimeAgo date={new Date(created_at)} c="dimmed" size="sm" style={{ whiteSpace: "nowrap" }} />
        )
      },
      {
        accessor: "actions",
        title: "",
        render: (item: ResearchReportListItem) => {
          const isAttached = attachedIds.has(item.report_id);
          const isAttachingThis =
            attachMutation.isPending && attachMutation.variables?.params.path.report_id === item.report_id;

          return (
            <Button
              size="compact-xs"
              color={isAttached ? "green" : undefined}
              leftSection={isAttached ? <Icon name="check" size="0.8rem" /> : undefined}
              loading={isAttachingThis}
              disabled={isAttached || (attachMutation.isPending && !isAttachingThis)}
              onClick={() => handleAttach(item.report_id)}
            >
              {isAttached ? "Attached" : "Attach"}
            </Button>
          );
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attachedIds, attachMutation.isPending, attachMutation.variables]
  );

  return (
    <>
      <ModalHeader title="Attach report" onClose={onClose} />
      <ModalContent>
        <Stack gap="sm">
          <Group gap="xs" wrap="nowrap">
            <Text size="sm" c="dimmed">
              Attach a report generated from another experiment.
            </Text>
            <Tooltip label="Looking for something new? Ask the agent to research it for you in the chat.">
              <Icon name="circle-question" size="0.9rem" color="dimmed" />
            </Tooltip>
          </Group>

          {isError && (
            <Alert color="red" title="Failed to load reports">
              {error instanceof Error ? error.message : "Unknown error"}
            </Alert>
          )}

          <TextInput
            placeholder="Search by experiment name…"
            value={nameFilter}
            onChange={e => {
              setNameFilter(e.currentTarget.value);
              debouncedSearch(e.currentTarget.value);
            }}
            autoFocus
          />

          <DataTable<ResearchReportListItem>
            columns={columns}
            records={data ?? []}
            fetching={isFetching}
            minHeight={150}
            noRecordsIcon={<Icon name="file-lines" size="1.2rem" style={{ marginBlock: 10 }} />}
            noRecordsText="No reports found"
          />
        </Stack>
      </ModalContent>
    </>
  );
}
